import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Form,
  Input,
  Button,
  Select,
  Upload,
  message,
  Typography,
  Row,
  Col,
  Spin
} from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import LocationSelector from "components/inputs/LocationSelector";
import warehouseApi from "../api/warehouseApi";
import "./EditWarehousePage.scss";

const { Text } = Typography;
const { Option } = Select;

const EditWarehousePage = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [warehouse, setWarehouse] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const history = useHistory();
  const { warehouseId } = useParams();

  // Load warehouse data on component mount
  useEffect(() => {
    loadWarehouse();
  }, [warehouseId]);

  const loadWarehouse = async () => {
    setInitialLoading(true);
    try {
      const foundWarehouse = await warehouseApi.getWarehouse(warehouseId);
      setWarehouse(foundWarehouse);

      form.setFieldsValue({
        warehouseName: foundWarehouse.name,
        category: foundWarehouse.category,
        image: foundWarehouse.image || null
      });

      if (foundWarehouse.image) {
        setImageFile(foundWarehouse.image);
      }

      // Set the selected location from savedLocation if available
      if (foundWarehouse.savedLocation) {
        setSelectedLocation({
          id: foundWarehouse.savedLocation.id,
          locationName: foundWarehouse.savedLocation.locationName,
          address: foundWarehouse.savedLocation.address,
          lat: foundWarehouse.savedLocation.lat,
          lng: foundWarehouse.savedLocation.lng,
          image: foundWarehouse.savedLocation.image?.url
        });
      } else if (foundWarehouse.address) {
        // Fallback for older warehouses without savedLocation
        setSelectedLocation({
          address: foundWarehouse.address
        });
      }
    } catch (error) {
      console.error("Error loading warehouse:", error);
      message.error("Failed to load warehouse data");
      history.push("/warehouses");
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      if (!selectedLocation) {
        message.error("Please select a location for the warehouse");
        setLoading(false);
        return;
      }

      const warehouseData = {
        name: values.warehouseName,
        category: values.category || "Distribution",
        address: selectedLocation.address,
        image: values.image || null,
        savedLocationId: selectedLocation.id || null
      };

      await warehouseApi.updateWarehouse(warehouseId, warehouseData);

      message.success("Warehouse updated successfully");
      history.push("/warehouses");
    } catch (error) {
      console.error("Error updating warehouse:", error);
      message.error("Failed to update warehouse");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    history.push("/warehouses");
  };

  const [imageFile, setImageFile] = useState(null);

  const handleFileUpload = (file) => {
    // Convert image to base64 data URL for persistent storage
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64Url = reader.result;
      setImageFile(base64Url);
      // Update form with image data
      form.setFieldValue("image", base64Url);
    };
    reader.readAsDataURL(file);
    return false; // Prevent automatic upload
  };

  if (initialLoading) {
    return (
      <div className="edit-warehouse-page loading-container">
        <Spin size="large" />
        <Text>Loading warehouse data...</Text>
      </div>
    );
  }

  if (!warehouse) {
    return (
      <div className="edit-warehouse-page error-container">
        <Text>Warehouse not found</Text>
      </div>
    );
  }

  return (
    <div className="edit-warehouse-page">
      <div className="page-header">
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={handleCancel}
          className="back-button"
        >
          Back to Warehouses
        </Button>
        <h1 className="page-title">Edit Warehouse</h1>
      </div>

      <div className="form-content">
        <Form
          id="warehouse-form"
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="warehouse-form"
        >
          <Row gutter={[24, 0]}>
            <Col span={12}>
              <Form.Item
                name="warehouseName"
                label="Warehouse Name"
                rules={[{ required: true, message: "Please enter warehouse name" }]}
              >
                <Input placeholder="Enter warehouse name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="category"
                label="Warehouse Category"
                rules={[{ required: true, message: "Please select a category" }]}
              >
                <Select placeholder="Select a Category">
                  <Option value="Distribution">Distribution</Option>
                  <Option value="Storage">Storage</Option>
                  <Option value="Manufacturing">Manufacturing</Option>
                  <Option value="Retail">Retail</Option>
                  <Option value="Other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[24, 0]}>
            <Col span={24}>
              <Form.Item label="Warehouse Location" required>
                <LocationSelector
                  value={selectedLocation}
                  onChange={setSelectedLocation}
                  placeholder="Select a saved location for this warehouse"
                />
              </Form.Item>
              {selectedLocation && (
                <div
                  className="selected-location-info"
                  style={{
                    marginTop: "8px",
                    padding: "12px",
                    backgroundColor: "#f6ffed",
                    border: "1px solid #b7eb8f",
                    borderRadius: "6px"
                  }}
                >
                  <div style={{ fontWeight: "bold", color: "#52c41a" }}>
                    📍 Selected Location:
                  </div>
                  <div style={{ marginTop: "4px" }}>
                    <strong>{selectedLocation.locationName}</strong>
                  </div>
                  <div style={{ color: "#666", fontSize: "14px" }}>
                    {selectedLocation.address}
                  </div>
                  {selectedLocation.lat && selectedLocation.lng && (
                    <div style={{ color: "#999", fontSize: "12px", marginTop: "4px" }}>
                      Coordinates: {selectedLocation.lat}, {selectedLocation.lng}
                    </div>
                  )}
                </div>
              )}
            </Col>
          </Row>
        </Form>

        <div className="form-footer">
          <Button onClick={handleCancel} className="cancel-button">
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            form="warehouse-form"
            loading={loading}
            className="save-button"
          >
            <SaveOutlined /> {loading ? "Updating..." : "Update Warehouse"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditWarehousePage;
